import { Currency, ETHER, Token } from '@uniswap/sdk'
import React, { useState } from 'react'
import styled from 'styled-components'
import { NATIVE_SYMBOL } from '../../chainConfig'

const BAD_URIS: { [tokenAddress: string]: true } = {}

const publicAssetPath = (path: string) => `${process.env.PUBLIC_URL || ''}${path}`
const sanitizeLogoName = (value?: string) => value?.toUpperCase().replace(/[^A-Z0-9]/g, '')
const localLogoURI = (value?: string) => {
  const fileName = sanitizeLogoName(value)
  return fileName ? publicAssetPath(`/images/logo/${fileName}.png`) : undefined
}

const addressLogoNames = (address: string): string[] => {
  const lowerAddress = address.toLowerCase()
  const upperAddress = address.toUpperCase()
  return [lowerAddress, address, upperAddress].filter(
    (fileName, index, fileNames) => fileNames.indexOf(fileName) === index
  )
}

const tokenLogoURIs = (token: Token): string[] =>
  [...addressLogoNames(token.address), sanitizeLogoName(token.symbol)]
    .filter((fileName, index, fileNames): fileName is string => Boolean(fileName) && fileNames.indexOf(fileName) === index)
    .map(fileName => publicAssetPath(`/images/logo/${fileName}.png`))

const Image = styled.img<{ size: string }>`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.075);
`

const Emoji = styled.span<{ size?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ size }) => size};
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  margin-bottom: -4px;
`

const StyledNativeLogo = styled.img<{ size: string }>`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.075);
  border-radius: 24px;
`

export default function CurrencyLogo({
  currency,
  size = '24px',
  ...rest
}: {
  currency?: Currency
  size?: string
  style?: React.CSSProperties
}) {
  const [, refresh] = useState<number>(0)

  if (currency === ETHER) {
    const uri = [localLogoURI(NATIVE_SYMBOL)].filter((item): item is string => Boolean(item) && !BAD_URIS[item])[0]
    if (!uri) {
      return (
        <Emoji {...rest} size={size}>
          <span role="img" aria-label="Thinking">
            🤔
          </span>
        </Emoji>
      )
    }

    return (
      <StyledNativeLogo
        src={uri}
        size={size}
        {...rest}
        onError={() => {
          if (uri) {
            BAD_URIS[uri] = true
          }
          refresh(i => i + 1)
        }}
      />
    )
  }

  if (currency instanceof Token) {
    const uri = tokenLogoURIs(currency).find(uri => !BAD_URIS[uri])

    if (uri) {
      return (
        <Image
          {...rest}
          alt={`${currency.name} Logo`}
          src={uri}
          size={size}
          onError={() => {
            BAD_URIS[uri] = true
            refresh(i => i + 1)
          }}
        />
      )
    }
  }

  return (
    <Emoji {...rest} size={size}>
      <span role="img" aria-label="Thinking">
        🤔
      </span>
    </Emoji>
  )
}
